/**
 * Adapter de síntesis de voz para Web
 *
 * Implementa VoiceOutputPort usando la Web Speech Synthesis API
 * (window.speechSynthesis / SpeechSynthesisUtterance).
 */

import type { VoiceOutputOptions, VoiceOutputPort } from '@pepi/core'

interface SpeechSynthesisUtteranceLike {
  lang: string
  onend: (() => void) | null
  onerror: ((event: unknown) => void) | null
}

type SpeechSynthesisUtteranceCtor = new (text: string) => SpeechSynthesisUtteranceLike

interface SpeechSynthesisLike {
  speak: (utterance: SpeechSynthesisUtteranceLike) => void
  cancel: () => void
}

function getSynthesis(): SpeechSynthesisLike | null {
  if (typeof window === 'undefined') return null
  return (window as unknown as { speechSynthesis?: SpeechSynthesisLike }).speechSynthesis ?? null
}

function getUtteranceCtor(): SpeechSynthesisUtteranceCtor | null {
  if (typeof window === 'undefined') return null
  return (
    (window as unknown as { SpeechSynthesisUtterance?: SpeechSynthesisUtteranceCtor })
      .SpeechSynthesisUtterance ?? null
  )
}

/**
 * Implementación de VoiceOutputPort para Web usando Web Speech Synthesis API
 */
export class WebVoiceOutputAdapter implements VoiceOutputPort {
  isSupported(): boolean {
    return getSynthesis() !== null && getUtteranceCtor() !== null
  }

  speak(text: string, options: VoiceOutputOptions = {}): void {
    const synthesis = getSynthesis()
    const Ctor = getUtteranceCtor()
    if (!synthesis || !Ctor) return

    const utterance = new Ctor(text)
    if (options.lang) {
      utterance.lang = options.lang
    }
    utterance.onend = () => options.onDone?.()
    utterance.onerror = (event: unknown) => options.onError?.(event)

    synthesis.speak(utterance)
  }

  stop(): void {
    getSynthesis()?.cancel()
  }
}

export function createWebVoiceOutputAdapter(): VoiceOutputPort {
  return new WebVoiceOutputAdapter()
}
